import type { CSSProperties, ComponentPropsWithRef, MouseEvent } from "react";
import { forwardRef, useCallback, useMemo } from "react";
import { Column, Link, Row, Section } from "@react-email/components";
import { omit } from "lodash";
import { parseSizing } from "@/react/components/shared/BuilderUI/utils/parseSizing";
import type { StyleValue } from "@circle-react-shared/BuilderUI/StyleValue.types";
import { TextRenderer } from "@circle-react-shared/Emails/VisualEmailBuilder/EditorFields/RichTextField/TextRenderer";
import { useParseStyles } from "../../../Design/useParseStyles";
import type { AlignmentOption } from "../../../ModifiedStyleAttributes/objectAlignment";
import { MSOWrapper } from "../../../Utils/MSOWrapper";
import type { MarginValues } from "../../../Utils/buttonStyleHelpers";
import {
  EMAIL_BUTTON_SECTION_PROPS,
  convertMarginToPadding,
  ensureSingleButtonBorder,
} from "../../../Utils/buttonStyleHelpers";
import { useResolvedButtonPadding } from "../../../Utils/useResolvedButtonPadding";
import { buildVmlButton } from "../../../Utils/vml/vmlBuilder";
import { defaultStyles } from "./defaultStyles";

const linkBaseStyles = {
  display: "inline-block",
  borderStyle: "solid",
  textDecoration: "none",
  textAlign: "center",
} as const;

interface ButtonProps extends ComponentPropsWithRef<"table"> {
  blockPath: string;
  "data-preview-mode": boolean;
  href: string;
  styles: Record<string, StyleValue>;
  text: string;
}

const { margin: defaultMargin, ...otherDefaultStyles } = defaultStyles;
const linkDefaultStyles = omit(otherDefaultStyles, ["sizing", "alignment"]);

export const SecondaryButton = forwardRef<HTMLAnchorElement, ButtonProps>(
  (
    {
      text,
      href,
      styles,
      blockPath,
      "data-preview-mode": isPreviewMode,
      ...rest
    },
    ref,
  ) => {
    const { alignment: styledAlignment, margin } = styles;
    const otherStyles = useMemo(() => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars, sonarjs/no-unused-vars -- we need to destructure the styles object to get the other styles
      const { alignment, margin: _margin, ...rest } = styles;
      return rest;
    }, [styles]);

    // this is a workaround to allow margin to be used as padding for the button section
    const sectionPaddingStyles = useMemo(
      () => ({
        padding: {
          type: margin.type,
          value: convertMarginToPadding(margin.value as MarginValues),
        },
      }),
      [margin],
    );
    const sectionDefaultPaddingStyles = useMemo(
      () => ({
        padding: convertMarginToPadding(defaultMargin),
      }),
      [],
    );

    const alignment = (styledAlignment?.value ||
      defaultStyles.alignment) as AlignmentOption;

    const tableStyles: CSSProperties = useParseStyles({
      styles: sectionPaddingStyles,
      defaultStyles: sectionDefaultPaddingStyles,
    });

    const linkParsedStyles: CSSProperties = useParseStyles({
      baseStyles: linkBaseStyles,
      styles: useMemo(
        () => omit(otherStyles, ["sizing", "alignment"]),
        [otherStyles],
      ),
      defaultStyles: linkDefaultStyles,
    });

    const linkStyles = {
      ...linkParsedStyles,
      ...parseSizing({ sizing: otherStyles.sizing }),
    } as CSSProperties;

    const resolvedPadding = useResolvedButtonPadding(
      otherDefaultStyles.padding,
    );

    ensureSingleButtonBorder(linkStyles);

    const { startComment: msoStartComment, endComment: msoEndComment } =
      useMemo(
        () =>
          buildVmlButton({
            linkStyles,
            padding: resolvedPadding,
            text,
            href,
          }),
        [linkStyles, resolvedPadding, text, href],
      );

    const handleClick = useCallback(
      (event: MouseEvent<HTMLAnchorElement>) => {
        if (isPreviewMode) event.preventDefault();
      },
      [isPreviewMode],
    );

    return (
      <Section style={tableStyles} {...EMAIL_BUTTON_SECTION_PROPS} {...rest}>
        <Row>
          <Column align={alignment}>
            <MSOWrapper
              startComment={msoStartComment}
              endComment={msoEndComment}
            >
              <Link
                ref={ref}
                href={href}
                onClick={handleClick}
                rel="noopener"
                style={linkStyles}
              >
                <TextRenderer
                  blockPath={blockPath}
                  text={text}
                  isPreviewMode={isPreviewMode}
                />
              </Link>
            </MSOWrapper>
          </Column>
        </Row>
      </Section>
    );
  },
);

SecondaryButton.displayName = "SecondaryButton";
